import type {UseMutationOptions} from '@tanstack/react-query';
import {postTicketBuy} from '../api/ticket/postTicketBuy';
import {postTicketBuyAndUse} from '../api/ticket/postTicketBuyAndUse';
import {postTicketFree} from '../api/ticket/postTicketFree';
import {postTicketSetBuy} from '../api/ticket/postTicketSetBuy';
import {postTicketUse} from '../api/ticket/postTicketUse';
import {TicketBuyParam} from '../common/shared/api/nicolas/params/TicketBuyParam.interface';
import {HelixTodayGiftDto} from '../common/shared/api/nicolas/vo/HelixTodayGiftDto.interface';
import {TodayGiftDto} from '../common/shared/api/nicolas/vo/TodayGiftDto.interface';
import {PagewebError} from '../errors/PagewebError.class';
import {createHelixTodayGift} from '../graphql/factory/HelixTodayGift';
import {createTicketBuyAndUseMutationPayload} from '../graphql/factory/TicketBuyAndUseMutationPayload';
import {createTicketBuyMutationPayload} from '../graphql/factory/TicketBuyMutationPayload';
import {createTicketSetBuyMutationPayload} from '../graphql/factory/TicketSetBuyMutationPayload';
import {createTicketTypeVo} from '../graphql/factory/TicketType';
import {createTicketUseMutationPayload} from '../graphql/factory/TicketUseMutationPayload';
import {createTodayGift} from '../graphql/factory/TodayGift';
import {
  BuyAndUseTicketMutation,
  BuyAndUseTicketMutationVariables,
  BuyTicketMutation,
  type BuyTicketMutationVariables,
  FreeTicketType,
  type ReceiveTicketFreeMutation,
  type ReceiveTicketFreeMutationVariables,
  SetBuyTicketMutation,
  SetBuyTicketMutationVariables,
  TicketKind,
  UseTicketMutation,
  UseTicketMutationVariables,
} from '../graphql/generated/types';

/**
 * Ticket 도메인 Mutation Adapter
 */
export const ticketMutationAdapter = {
  /**
   * 이용권 구매
   *
   * ⚠️ Resolver 로직 복제:
   * 1) input.ticketList 배열을 객체로 reduce
   * 2) ticketKind에 따라 type 변환 (Own=1, 대여=2)
   * 3) postTicketBuy에서 특수 로직: ticket_list JSON에서 property의 double quote 제거
   * 4) Factory 함수로 응답 변환
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(ticketMutationAdapter.buyTicket());
   * mutation.mutate({
   *   input: {
   *     seriesId: 123,
   *     ticketList: [{ticketId: 'ticket1', quantity: 2}],
   *     ticketKind: TicketKind.Own
   *   }
   * });
   * ```
   */
  buyTicket: (): UseMutationOptions<
    BuyTicketMutation,
    PagewebError,
    BuyTicketMutationVariables
  > => ({
    mutationFn: async (variables: BuyTicketMutationVariables) => {
      // Resolver 로직: ticketList 배열을 객체로 변환
      const param: TicketBuyParam = {
        seriesId: variables.input.seriesId,
        ticketList: variables.input.ticketList.reduce(
          (previousValue, currentValue) => ({
            ...previousValue,
            [currentValue.ticketId]: currentValue.quantity,
          }),
          {}
        ),
        type: variables.input.ticketKind === TicketKind.Own ? 1 : 2,
      };

      const {result} = await postTicketBuy(param);
      return {buyTicket: createTicketBuyMutationPayload(result)};
    },
  }),

  /**
   * 무료 이용권 수령
   *
   * Resolver 로직 복제:
   * - pageNicolasApi.receiveFreeTicket 호출 결과의 result를 분기하여 팩토리 적용
   */
  receiveTicketFree: (): UseMutationOptions<
    ReceiveTicketFreeMutation,
    PagewebError,
    ReceiveTicketFreeMutationVariables
  > => ({
    mutationFn: async (variables: ReceiveTicketFreeMutationVariables) => {
      const {result} = await postTicketFree(variables.input);
      if (variables.input.type === FreeTicketType.HelixGift) {
        return {
          receiveTicketFree: createHelixTodayGift(result as HelixTodayGiftDto),
        };
      }
      return {receiveTicketFree: createTodayGift(result as TodayGiftDto)};
    },
  }),

  /**
   * 세트 이용권 구매
   *
   * ⚠️ Resolver 로직:
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공 시 setBuyTicket 결과 반환
   * - React Query의 error state 사용
   *
   * 사용법:
   * ```tsx
   * const options = ticketMutationAdapter.setBuyTicket();
   * const result = await queryClient
   *   .getMutationCache()
   *   .build(queryClient, options)
   *   .execute(variables);
   * ```
   */
  setBuyTicket: (): UseMutationOptions<
    SetBuyTicketMutation,
    PagewebError,
    SetBuyTicketMutationVariables
  > => ({
    mutationKey: ['SetBuyTicket'],
    mutationFn: async (variables?: SetBuyTicketMutationVariables) => {
      if (!variables) {
        throw new Error('variables is required');
      }
      const {result} = await postTicketSetBuy(variables.input);
      return {setBuyTicket: createTicketSetBuyMutationPayload(result)};
    },
  }),

  /**
   * 이용권 구매 및 사용 (즉시 사용)
   *
   * ⚠️ Resolver 로직:
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공 시 buyAndUseTicket 결과 반환
   * - ticketUid를 포함한 결과 반환
   * - React Query의 error state 사용
   *
   * 사용법:
   * ```tsx
   * const options = ticketMutationAdapter.buyAndUseTicket();
   * const result = await queryClient
   *   .getMutationCache()
   *   .build(queryClient, options)
   *   .execute(variables);
   * ```
   */
  buyAndUseTicket: (): UseMutationOptions<
    BuyAndUseTicketMutation,
    PagewebError,
    BuyAndUseTicketMutationVariables
  > => ({
    mutationKey: ['BuyAndUseTicket'],
    mutationFn: async (variables?: BuyAndUseTicketMutationVariables) => {
      if (!variables) {
        throw new Error('variables is required');
      }
      const param = {
        productId: variables.input.productId,
        ticketId: variables.input.ticketId,
      };
      const {result} = await postTicketBuyAndUse(param);
      return {
        buyAndUseTicket: createTicketBuyAndUseMutationPayload(result),
      };
    },
  }),

  /**
   * 이용권 사용
   *
   * ⚠️ Resolver 로직:
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공 시 useTicket 결과 반환
   * - ticketUid를 포함한 결과 반환
   * - React Query의 error state 사용
   *
   * 사용법:
   * ```tsx
   * const options = ticketMutationAdapter.useTicket();
   * const result = await queryClient
   *   .getMutationCache()
   *   .build(queryClient, options)
   *   .execute(variables);
   * ```
   */
  useTicket: (): UseMutationOptions<
    UseTicketMutation,
    PagewebError,
    UseTicketMutationVariables
  > => ({
    mutationKey: ['UseTicket'],
    mutationFn: async (variables?: UseTicketMutationVariables) => {
      if (!variables) {
        throw new Error('variables is required');
      }
      const param = {
        productId: variables.input.productId,
        ticketType: variables.input.ticketType
          ? createTicketTypeVo(variables.input.ticketType)
          : undefined,
      };
      const {result} = await postTicketUse(param);
      return {useTicket: createTicketUseMutationPayload(result || {})};
    },
  }),
} as const;
